import React, { useState } from 'react';
import { TextField, styled, Typography, Autocomplete, useTheme, Box, Link } from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { useTranslation } from 'react-i18next';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { ExpandMore } from '@mui/icons-material';
import { Fund, useSearchFunds, useGetFunds } from '../../api/useFunds';
import { CloseIcon } from '../../components/CloseIcon';
import { Divider } from '../../components/Divider';

const StyledLink = styled(Link)(() => ({
  textDecoration: 'none',
  cursor: 'pointer',
}));

const ClearAll = styled(Typography)(({ theme }) => ({
  color: theme.palette.action.active,
  cursor: 'pointer',
  textAlign: 'right',
}));

const AutocompleteContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ theme, fullWidth }) => ({
  display: 'flex',
  alignItems: 'center',
  width: fullWidth ? '100%' : '480px',

  [theme.breakpoints.only('xs')]: {
    width: '100%',
  },

  '& .MuiAutocomplete-paper': {
    borderRadius: '6px',
    boxShadow: '0px 2px 4px -1px rgba(30, 39, 50, 0.16)',
    border: `1px solid ${theme.palette.border.secondary}`,
  },

  '& .MuiInputLabel-root': {
    marginTop: '-4px',
    fontSize: '14px',
  },

  '& .MuiInputBase-root': {
    padding: '3px 0 !important',
  },

  '& .MuiAutocomplete-clearIndicator svg, & .MuiAutocomplete-popupIndicator svg': {
    width: '22px',
    height: '22px',
  },

  '& .MuiAutocomplete-clearIndicator': {
    padding: theme.spacing(0.5, 1),
  },

  '& .MuiAutocomplete-input': {
    padding: `${theme.spacing(2, 16, 2, 4)} !important`,

    '&::placeholder': {
      opacity: 1,
      fontSize: '14px',
      color: theme.palette.text.primary,
    },
  },

  '.MuiAutocomplete-groupLabel': {
    fontSize: '12px',
    lineHeight: '18px',
    padding: theme.spacing(2, 3, 1, 3),
    fontWeight: '600',
  },

  '& .MuiAutocomplete-loading, & .MuiAutocomplete-noOptions': {
    fontSize: '14px',
  },
}));

const StyledTextField = styled(TextField)(() => ({
  '& input': {
    height: '30px',
  },
}));

const filterOptions = createFilterOptions<Fund>({
  matchFrom: 'any',
  stringify: (option) => `${option.name} ${option.isin} ${option.wkn}`,
});

const Option = styled('li')(({ theme }) => ({
  padding: `${theme.spacing(1.5, 3, 1.5, 3)} !important`,
  flexDirection: 'column',
  alignItems: 'flex-start !important',
}));

const Highlight = ({
  parts,
}: {
  parts: {
    text: string;
    highlight: boolean;
  }[];
}) => {
  const theme = useTheme();

  return (
    <>
      {parts.map((part, index) => (
        <span
          key={index}
          style={part.highlight ? { backgroundColor: theme.palette.background.cashback } : {}}
        >
          {part.text}
        </span>
      ))}
    </>
  );
};

interface Props {
  id: string;
  disabled?: boolean;
  selectedFund?: Fund | null;
  value?: string;
  mode?: 'simple' | 'advanced';
  autocompleteKey?: string;
  onChange: (fund: Fund) => void;
  onClearAll?: () => void;
  areFundsSelected?: boolean;
  fullWidth?: boolean;
}

export const FundAutocomplete = ({
  id,
  selectedFund,
  disabled = false,
  fullWidth = false,
  mode = 'advanced',
  autocompleteKey,
  areFundsSelected,
  onClearAll,
  onChange,
}: Props) => {
  const theme = useTheme();
  const [search, setSearch] = useState<string | undefined>();
  const [showAutocomplete, setShowAutocomplete] = useState(mode === 'simple' || !areFundsSelected);
  const { t } = useTranslation();
  const { status, data: fundList } = useSearchFunds(search);
  // DE0009848119: DWS Top Dividende LD
  // LU0323578657: Flossbach von Storch SICAV - Multiple Opportunities R
  // DE0009769869: DWS Aktien Strategie Deutschland LC

  const { data: mostUsedFundList } = useGetFunds(['DE0009848119', 'LU0323578657', 'DE0009769869']);

  const handleInputChange = (value: string) => {
    setSearch(value);
  };

  const handleOnChange = (fund: any) => {
    onChange(fund as Fund);
    setShowAutocomplete(false);
  };

  const getGroup = (isin?: string) => {
    switch (isin) {
      case 'DE0009848119':
        return t('cashbackCalculator.detailed.fundAutocomplete.mostUsed');
      case 'LU0323578657':
        return t('cashbackCalculator.detailed.fundAutocomplete.mostUsed');
      case 'DE0009769869':
        return t('cashbackCalculator.detailed.fundAutocomplete.mostUsed');
      default:
        return t('cashbackCalculator.detailed.fundAutocomplete.others');
    }
  };

  return (
    <>
      {showAutocomplete && areFundsSelected && <Divider />}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: showAutocomplete && areFundsSelected ? theme.spacing(4) : 0,
        }}
      >
        {!showAutocomplete && areFundsSelected && (
          <StyledLink onClick={() => setShowAutocomplete(true)} data-testid="add-another-fund">
            <Typography variant="body2">
              {t('cashbackCalculator.detailed.fundAutocomplete.addMore')}
            </Typography>
          </StyledLink>
        )}
        {(showAutocomplete || !areFundsSelected) && (
          <AutocompleteContainer fullWidth={fullWidth}>
            <Autocomplete<Fund>
              disablePortal
              id={id}
              disabled={disabled}
              filterOptions={filterOptions}
              data-testid="fund-auto"
              key={autocompleteKey}
              blurOnSelect
              fullWidth
              defaultValue={selectedFund}
              loading={status === 'loading'}
              onInputChange={(_event, value) => handleInputChange(value)}
              onChange={(_: any, value: any) => handleOnChange(value)}
              options={fundList?.funds || mostUsedFundList?.funds || []}
              getOptionLabel={(option) => {
                return option.name || '';
              }}
              isOptionEqualToValue={(option, value) => {
                return option.isin === value.isin;
              }}
              renderOption={(props, option, { inputValue }) => {
                const matchesName = match(option.name || '', inputValue);
                const partsName = parse(option.name || '', matchesName);
                const matchesIsin = match(option.isin || '', inputValue);
                const partsIsin = parse(option.isin || '', matchesIsin);
                const matchesWkn = match(option?.wkn || '', inputValue);
                const partsWkn = parse(option?.wkn || '', matchesWkn);

                return (
                  <Option {...props}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                      data-testid={`fund-autocomplete-option-${option.isin}`}
                    >
                      <Highlight parts={partsName} />
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      ISIN: <Highlight parts={partsIsin} /> • WKN: <Highlight parts={partsWkn} />
                    </Typography>
                  </Option>
                );
              }}
              clearOnEscape
              groupBy={(option) => getGroup(option.isin)}
              popupIcon={
                <ExpandMore
                  sx={{
                    marginTop: '2px',
                    color: theme.palette.text.primary,
                  }}
                />
              }
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  disabled={disabled}
                  placeholder={t('cashbackCalculator.detailed.fundAutocomplete.placeholder')}
                  variant="outlined"
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'fund-autocomplete-input',
                    'data-hj-whitelist': true,
                  }}
                />
              )}
            />
            {areFundsSelected && (
              <CloseIcon
                onClick={() => setShowAutocomplete(false)}
                sx={{ marginLeft: theme.spacing(3) }}
                data-testid="close-autocomplete"
              />
            )}
          </AutocompleteContainer>
        )}
        {mode === 'advanced' && areFundsSelected && (
          <ClearAll variant="body2" onClick={onClearAll} data-testid="clear-funds">
            {t('cashbackCalculator.detailed.calculator.clearAll')}
          </ClearAll>
        )}
      </Box>
    </>
  );
};
